import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  MapPin, Star, Users, Clock, ArrowLeft, Wine, Sparkles, CheckCircle, Wifi, Music, Car, Utensils, Shield
} from "lucide-react";
import VenueGallery from "@/components/VenueGallery";
import { Skeleton } from "@/components/ui/skeleton";

const amenityIcons: Record<string, any> = {
  wifi: Wifi, "live-music": Music, parking: Car, food: Utensils, vip: Shield, "bottle-service": Wine,
};

const VenueDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const [venue, setVenue] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [userKrews, setUserKrews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const fetchAll = async () => {
      const { data: venueData } = await supabase.from("venues").select("*").eq("slug", slug).single();
      if (venueData) {
        setVenue(venueData);
        const [reviewsRes, krewsRes] = await Promise.all([
          supabase.from("venue_reviews").select("*, profiles:user_id(display_name)").eq("venue_id", venueData.id).order("created_at", { ascending: false }),
          user ? supabase.from("krews").select("id, title, status").eq("creator_id", user.id).in("status", ["planning", "venue_voting"]) : Promise.resolve({ data: [] }),
        ]);
        if (reviewsRes.data) setReviews(reviewsRes.data);
        if (krewsRes.data) setUserKrews(krewsRes.data || []);

        // Generate AI review summary if enough reviews
        if (reviewsRes.data && reviewsRes.data.length >= 3) {
          try {
            const { data } = await supabase.functions.invoke("ai-krew-assistant", {
              body: {
                messages: [{ role: "user", content: `Summarize these venue reviews in 2 concise sentences: ${reviewsRes.data.slice(0, 10).map(r => `"${r.review}" (${r.rating}/5)`).join("; ")}` }],
                context: { venueName: venueData.name, city: venueData.city },
              },
            });
            // Non-streaming fallback - parse the response
            if (typeof data === "string" && data.includes("data: ")) {
              const lines = data.split("\n").filter((l: string) => l.startsWith("data: ") && l !== "data: [DONE]");
              let summary = "";
              for (const line of lines) {
                try {
                  const parsed = JSON.parse(line.slice(6));
                  summary += parsed.choices?.[0]?.delta?.content || "";
                } catch {}
              }
              if (summary) setAiSummary(summary);
            }
          } catch {}
        }
      }
      setLoading(false);
    };
    fetchAll();
  }, [slug, user]);

  const submitReview = async () => {
    if (!user || !venue || !rating) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("venue_reviews").insert({ venue_id: venue.id, user_id: user.id, rating, review: reviewText });
      if (error) throw error;
      toast({ title: "Review submitted! ⭐" });
      setRating(0); setReviewText("");
      const { data } = await supabase.from("venue_reviews").select("*, profiles:user_id(display_name)").eq("venue_id", venue.id).order("created_at", { ascending: false });
      if (data) setReviews(data);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const selectVenueForKrew = async (krewId: string) => {
    if (!venue) return;
    const { error } = await supabase.from("krews").update({ selected_venue_id: venue.id, status: "committed" }).eq("id", krewId);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Venue selected! 🎯", description: `${venue.name} is now your Krew's venue.` });
    setUserKrews(prev => prev.filter(k => k.id !== krewId));
  };

  const liveRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : venue?.rating;
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 5);

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="aspect-[21/9] w-full rounded-2xl" />
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-20 w-full" />
    </div>
  );

  if (!venue) return <div className="py-20 text-center text-muted-foreground">Venue not found</div>;

  const galleryImages = [venue.image_url, ...(venue.gallery || [])].filter(Boolean);

  return (
    <div className="space-y-8">
      <button onClick={() => window.history.back()} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Gallery */}
      {galleryImages.length > 1 ? (
        <VenueGallery images={galleryImages} venueName={venue.name} />
      ) : (
        <div className="overflow-hidden rounded-2xl">
          <img src={venue.image_url || "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=1200"} alt={venue.name} className="aspect-[21/9] w-full object-cover" />
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-1 space-y-6">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{venue.name}</h1>
              <div className="flex items-center gap-1 rounded-full bg-secondary/20 px-3 py-1">
                <Star className="h-4 w-4 text-secondary" />
                <span className="text-sm font-bold text-secondary">{liveRating}</span>
                <span className="text-xs text-muted-foreground">({reviews.length})</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" /><span className="text-sm">{venue.address}</span>
            </div>
          </div>

          <p className="text-muted-foreground font-body leading-relaxed">{venue.description}</p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4 text-center">
              <Users className="mx-auto mb-1 h-5 w-5 text-primary" />
              <p className="text-lg font-bold text-foreground">{venue.capacity}</p>
              <p className="text-xs text-muted-foreground">Capacity</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 text-center">
              <Wine className="mx-auto mb-1 h-5 w-5 text-secondary" />
              <p className="text-lg font-bold text-gradient-gold">₹{venue.bottle_service_min?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Bottle Min</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 text-center">
              <Clock className="mx-auto mb-1 h-5 w-5 text-primary" />
              <p className="text-lg font-bold text-foreground">{venue.opening_time?.slice(0, 5)} – {venue.closing_time?.slice(0, 5)}</p>
              <p className="text-xs text-muted-foreground">Hours</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 text-center">
              <Sparkles className="mx-auto mb-1 h-5 w-5 text-primary" />
              <p className="text-lg font-bold text-foreground capitalize">{venue.category}</p>
              <p className="text-xs text-muted-foreground">Type</p>
            </div>
          </div>

          {/* Amenities */}
          {venue.amenities?.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {venue.amenities.map((a: string) => {
                  const Icon = amenityIcons[a] || CheckCircle;
                  return (
                    <span key={a} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground">
                      <Icon className="h-3.5 w-3.5 text-primary" />{a}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vibes */}
          {venue.vibe?.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Vibes</h3>
              <div className="flex flex-wrap gap-2">
                {venue.vibe.map((v: string) => (
                  <span key={v} className="rounded-full bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">{v}</span>
                ))}
              </div>
            </div>
          )}

          {/* AI Review Summary */}
          {aiSummary && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">AI Review Summary</span>
              </div>
              <p className="text-sm text-muted-foreground italic">{aiSummary}</p>
            </div>
          )}

          {/* Reviews */}
          <div>
            <h3 className="mb-4 text-xl font-bold text-foreground">Reviews ({reviews.length})</h3>
            {user && (
              <div className="mb-6 rounded-xl border border-border bg-card p-5">
                <div className="mb-3 flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} onClick={() => setRating(s)}>
                      <Star className={`h-6 w-6 transition-colors ${s <= rating ? "fill-secondary text-secondary" : "text-muted-foreground"}`} />
                    </button>
                  ))}
                </div>
                <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="How was your experience?" rows={2} className="mb-3 w-full rounded-lg border border-border bg-muted px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none resize-none" />
                <button onClick={submitReview} disabled={!rating || submitting} className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Submit Review</button>
              </div>
            )}
            <div className="space-y-4">
              {displayedReviews.map((review: any) => (
                <div key={review.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{(review.profiles as any)?.display_name || "Anonymous"}</span>
                      {review.krew_id && <CheckCircle className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s <= review.rating ? "fill-secondary text-secondary" : "text-muted"}`} />
                      ))}
                    </div>
                  </div>
                  {review.review && <p className="text-sm text-muted-foreground">{review.review}</p>}
                </div>
              ))}
              {reviews.length > 5 && !showAllReviews && (
                <button onClick={() => setShowAllReviews(true)} className="w-full rounded-lg border border-border py-2 text-sm text-muted-foreground hover:text-foreground">
                  Load more reviews ({reviews.length - 5} more)
                </button>
              )}
              {reviews.length === 0 && <p className="text-center text-sm text-muted-foreground">No reviews yet. Be the first!</p>}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80">
          <div className="sticky top-6 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-bold text-foreground">Book for your Krew</h3>
              <p className="mb-4 text-sm text-muted-foreground">Average ₹{venue.avg_price_per_person.toLocaleString()} per person</p>
              {userKrews.length > 0 ? (
                <div className="space-y-2">
                  {userKrews.map(k => (
                    <button key={k.id} onClick={() => selectVenueForKrew(k.id)} className="flex w-full items-center justify-between rounded-xl border border-border bg-muted px-4 py-3 text-sm hover:border-primary/30 transition-all">
                      <span className="font-medium text-foreground">{k.title}</span>
                      <span className="text-xs text-primary">Select →</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl bg-primary/10 p-4 text-center">
                  <p className="text-sm text-primary mb-2">Create a Krew to book this venue!</p>
                  <Link to="/create-krew" className="inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Create Krew</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueDetail;
